import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Link, useNavigate, useParams } from 'react-router-dom'
import dayjs from 'dayjs'
import { ChevronRight, FilePenLine, Search, X } from 'lucide-react'

import { UserDisplay } from '@/components'
import { EmptySearchResult } from '@/components/EmptySearchResult'
import { Table } from '@/components/Table'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Separator } from '@/components/ui/separator'
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip'
import CompanyDialog from '@/features/company/components/CompanyDialog'
import CompanyHistoryDrawer from '@/features/company/components/CompanyHistoryDrawer'
import { useCompanyById } from '@/features/company/hooks'
import { useCompanyStore } from '@/features/company/store'
import { useProjectList, useProjectsStore } from '@/features/project'
import { getProjectColumns, ProjectAction } from '@/features/project/columns'
import DeleteProjectDialog from '@/features/project/components/DeleteProjectDialog'
import ProjectDialog from '@/features/project/components/ProjectDialog'
import { Project } from '@/features/project/types'
import { ERoleType } from '@/features/user-management/constants'
import { useDebounce } from '@/hooks/useDebounce'
import { SortOrder } from '@/types/common'
import {
  DATE_FORMAT,
  MIN_SEARCH_LENGTH,
  SEARCH_DEBOUNCE_DELAY_MS,
} from '@/utils/constants'
import { buildProjectRoute, ROUTES } from '@/utils/routes'

import { TitleText } from '../../components'
import { useAuthStore } from '../../features/auth'

const CompanyDetailPage: React.FC = () => {
  const { t } = useTranslation()
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const { data, isLoading, error } = useCompanyById(id || '')
  const { setEditingCompany } = useCompanyStore()
  const company = data?.[0]

  const {
    page,
    rowsPerPage,
    sort,
    setPage,
    setSort,
    setEditingProject,
    setDeletingProject,
    setDialogOpen: setProjectDialogOpen,
    deletingProject,
    dialogOpen,
    reset: resetProjectStore,
  } = useProjectsStore()

  const [searchText, setSearchText] = useState<string>('')
  const [historyOpen, setHistoryOpen] = useState<boolean>(false)
  const debouncedSearchText = useDebounce(searchText, SEARCH_DEBOUNCE_DELAY_MS)

  const roles = useAuthStore((state) => state.roles)
  const canViewHistory = roles?.some(
    (role) => role.role_name === ERoleType.ORGANIZATION_ADMIN
  )

  useEffect(() => {
    resetProjectStore()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const searchQuery = useMemo(() => {
    const trimmed = debouncedSearchText?.trim() || ''
    return trimmed.length >= MIN_SEARCH_LENGTH ? trimmed : ''
  }, [debouncedSearchText])

  const stableFilters = useMemo(
    () => ({
      searchName: searchQuery,
      companyId: id || null,
      companyName: null,
    }),
    [searchQuery, id]
  )

  const stableSort = useMemo(
    () => ({
      column: sort.column,
      direction: sort.direction,
    }),
    [sort.column, sort.direction]
  )

  const { data: projectsData, isLoading: isLoadingProjects } = useProjectList({
    search: searchQuery,
    page,
    rowsPerPage,
    sort: stableSort,
    filters: stableFilters,
  })

  // Reset page to 0 when search changes
  useEffect(() => {
    setPage(0)
  }, [searchQuery, setPage])

  // Reset page to 0 when sort changes
  useEffect(() => {
    setPage(0)
  }, [sort.column, sort.direction, setPage])

  // Empty state message for search results
  const noDataMessage = useMemo(() => {
    const isSearching = Boolean(searchQuery)
    if (isSearching) {
      return <EmptySearchResult translationKey="project" />
    }
    return null
  }, [searchQuery])

  const projects = projectsData?.data || []
  const totalCount = projectsData?.metadata?.total || 0
  const totalPages = Math.ceil(totalCount / rowsPerPage)

  const handleActionClick = useCallback(
    (action: ProjectAction, item: Project) => {
      switch (action) {
        case 'view-detail':
          navigate(buildProjectRoute(item.id))
          break
        case 'edit':
          setEditingProject(item)
          break
        case 'delete':
          setDeletingProject(item)
          break
        default:
          break
      }
    },
    [navigate, setEditingProject, setDeletingProject]
  )

  const columns = useMemo(
    () => getProjectColumns(handleActionClick, t),
    [handleActionClick, t]
  )

  const handleSortChange = useCallback(
    (colId: keyof Project | null, dir: SortOrder) => {
      setSort({
        column: colId,
        direction: dir,
      })
    },
    [setSort]
  )

  const handleSearchChange = useCallback((value: string) => {
    setSearchText(value)
  }, [])

  if (isLoading) {
    return (
      <div className="flex justify-center items-center min-h-50">
        <div className="w-12 h-12 border-b-2 border-blue-600 rounded-full animate-spin" />
      </div>
    )
  }

  if (error || !company) {
    return (
      <div className="space-y-6">
        <div>
          <h1 className="m-0 mb-1 font-semibold text-display-xs">
            {t('company.page.detailTitle')}
          </h1>
          <p className="m-0 text-red-400 text-md">
            {t('company.page.notFound')}
          </p>
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Breadcrumb + History button */}
      <div className="flex items-center justify-between">
        <div className="flex items-start gap-2 text-sm text-muted-foreground">
          <Link
            to={ROUTES.COMPANY}
            className="flex-shrink-0 font-semibold no-underline text-quaternary-500 hover:text-quaternary-600"
          >
            {t('sidebar.subItem.company')}
          </Link>
          <ChevronRight
            size={16}
            className="flex-shrink-0 text-gray-light-300"
          />
          <span className="min-w-0 font-semibold break-all text-primary-900">
            {company.name}
          </span>
        </div>
        {canViewHistory && (
          <Button
            variant="outlined"
            className="text-sm text-quaternary-600"
            onClick={() => setHistoryOpen(true)}
          >
            {t('company.actions.history')}
          </Button>
        )}
      </div>

      {/* Page Title */}
      <TitleText>{company.name}</TitleText>

      {/* Info Box */}
      <div className="bg-white border border-solid shadow-sm border-secondary rounded-xl">
        <div className="flex items-center justify-between px-6 py-4">
          <h2 className="m-0 text-lg font-semibold text-primary-900">
            {t('company.detail.title')}
          </h2>
          <Tooltip delayDuration={0}>
            <TooltipTrigger asChild>
              <Button
                variant="text"
                className="w-5 h-5 bg-transparent border-none text-foreground-fg-quaternary-400 hover:bg-transparent"
                onClick={() => setEditingCompany(company)}
              >
                <FilePenLine size={20} />
              </Button>
            </TooltipTrigger>
            <TooltipContent>
              <span>{t('company.actions.edit')}</span>
            </TooltipContent>
          </Tooltip>
        </div>

        <Separator />

        <div className="grid grid-cols-3 gap-4 px-6 py-4 text-sm">
          <div className="flex flex-col gap-1">
            <span className="text-secondary-700">
              {t('company.detail.name')}
            </span>
            <span className="font-semibold break-all">{company.name}</span>
          </div>

          <div className="flex flex-col gap-1">
            <span className="text-secondary-700">
              {t('company.detail.createdBy')}
            </span>
            <span className="font-medium">
              <UserDisplay user={company?.created_by_user} showAvatar={false} />
            </span>
          </div>
          <div className="flex flex-col gap-1">
            <span className="text-secondary-700">
              {t('company.detail.createdDate')}
            </span>
            <span className="font-medium">
              {dayjs(company.created_at).format(DATE_FORMAT)}
            </span>
          </div>
        </div>
      </div>

      <div className="bg-white border border-solid shadow-sm border-secondary rounded-xl">
        <div className="flex items-center justify-between border-t border-b-0 border-l border-r border-solid border-[--Colors-Gray-light-300] rounded-t-xl">
          <div className="px-[--spacing-2xl] py-[--spacing-xl] flex items-center justify-between w-full">
            <h2 className="m-0 text-lg font-semibold text-primary-900">
              {t('company.detail.projects')}
            </h2>
            <div className="flex items-center gap-3">
              <Input
                type="text"
                leadingIcon={<Search size={20} className="text-gray-400" />}
                placeholder={t('project.filters.searchPlaceholder')}
                value={searchText}
                onChange={(e) => handleSearchChange(e.target.value)}
                className="w-64"
                trailingIcon={
                  searchText ? (
                    <X className="w-3.5 h-3.5 cursor-pointer" />
                  ) : undefined
                }
                onTrailingIconClick={
                  searchText ? () => setSearchText('') : undefined
                }
              />
              <Button
                variant="solid"
                onClick={() => setProjectDialogOpen(true, company.id)}
                className="w-full text-sm"
              >
                {t('project.actions.addNew')}
              </Button>
            </div>
          </div>
        </div>

        <Table
          data={projects}
          columns={columns}
          isLoading={isLoadingProjects}
          page={page}
          totalPages={totalPages}
          onPageChange={setPage}
          noDataMessage={noDataMessage}
          orderBy={sort.column}
          order={sort.direction}
          onSortChange={handleSortChange}
          onRowClick={(row) => handleActionClick('view-detail', row)}
        />
      </div>

      <CompanyDialog />
      {dialogOpen && <ProjectDialog />}
      {deletingProject && <DeleteProjectDialog />}
      <CompanyHistoryDrawer
        open={historyOpen}
        onOpenChange={setHistoryOpen}
        entityId={company.id}
      />
    </div>
  )
}

export default CompanyDetailPage
